import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const AuthenticationModal = ({ 
  isOpen, 
  onClose, 
  onAuthenticate,
  title = '회원 빠른 인증',
  description = '등록된 전화번호를 입력하세요'
}) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        firstInputRef?.current?.focus();
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
      setName('');
      setPhoneNumber('');
      setError('');
      setIsLoading(false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      handleClose();
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  };

  const formatPhoneNumber = (value) => {
    const numbers = value?.replace(/[^\d]/g, '');
    
    if (numbers?.length <= 3) {
      return numbers;
    } else if (numbers?.length <= 7) {
      return `${numbers?.slice(0, 3)}-${numbers?.slice(3)}`;
    } else {
      return `${numbers?.slice(0, 3)}-${numbers?.slice(3, 7)}-${numbers?.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e?.target?.value);
    setPhoneNumber(formatted);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!String(name || '').trim()) {
      setError('이름을 입력해주세요');
      return;
    }
    
    if (!phoneNumber?.trim()) {
      setError('전화번호를 입력해주세요');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onAuthenticate({ name: String(name || '').trim(), phone: phoneNumber });
    } catch (err) {
      setError(err?.message || '인증에 실패했습니다. 다시 시도해주세요.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[6vh] px-4"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
      
      <div
        ref={modalRef}
        className="
          relative z-20 w-full max-w-md
          bg-card rounded-xl shadow-2xl
          border border-border
          animate-in fade-in slide-in-from-top-4
          duration-250
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h2 
                id="modal-title" 
                className="text-2xl font-heading font-semibold text-foreground mb-2"
              >
                {title}
              </h2>
              <p className="text-sm caption text-muted-foreground">
                {description}
              </p>
            </div>
            
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="
                ml-4 p-2 rounded-md
                text-muted-foreground hover:text-foreground
                hover:bg-muted
                transition-athletic active-press
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              aria-label="모달 닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              ref={firstInputRef}
              type="text"
              label="이름"
              placeholder="홍길동"
              value={name}
              onChange={(e) => {
                setName(e?.target?.value);
                if (error) setError('');
              }}
              error={error}
              required
              disabled={isLoading}
              description="회원 등록된 이름을 입력해주세요"
            />

            <Input
              type="tel"
              label="전화번호"
              placeholder="010-1234-5678"
              value={phoneNumber}
              onChange={handlePhoneChange}
              error={error}
              required
              disabled={isLoading}
              description="등록된 전화번호를 입력해주세요"
            />

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                fullWidth
              >
                취소
              </Button>
              
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="LogIn"
                iconPosition="right"
                fullWidth
              >
                확인
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-sm caption text-muted-foreground">
                  아직 회원이 아니신가요? 시설 관리자에게 문의하여 회원 등록을 진행해주세요.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationModal;